import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Modal, Form, Input, Row, Col, message, Button } from 'antd';

import {
  MUTATION_CREATE_PMS_CONTACT,
  MUTATION_UPDATE_PMS_CONTACT,
} from '@pages/dashboard/pms-integrations/quires';
import { validateEmail, validateNotEmpty, validateInternationalTelephone } from '~helpers/validate';

import PhoneNumber from '~components/phone-number';

function ContactModal({ t, onClose, visible, form, type, refetch, contactInfo, pmsId }) {
  const { getFieldDecorator, resetFields, validateFields } = form;

  const [createPMSContact, { loading: createLoading }] = useMutation(MUTATION_CREATE_PMS_CONTACT, {
    onCompleted: res => {
      if (res?.createPMSContact?.contact?.id) {
        resetFields();
        onClose();
        refetch();
      }
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      } else {
        message.error(t('Something wrong, please try again.'));
      }
    },
  });

  const [updatePMSContact, { loading: updateLoading }] = useMutation(MUTATION_UPDATE_PMS_CONTACT, {
    onCompleted: result => {
      if (result?.updatePMSContact?.contact?.id) {
        resetFields();
        onClose();
        refetch();
      }
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      } else {
        message.error(t('Something wrong, please try again.'));
      }
    },
  });

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const { contactEmail, contactName, contactPhone, occupation } = values;
        const params = {
          variables: {
            input: {
              contactName,
              contactEmail,
              contactPhone,
              occupation,
            },
          },
        };

        if (type === 'edit') {
          params.variables.input.id = contactInfo.id;
          updatePMSContact(params);
        } else {
          params.variables.input.pmsId = pmsId;
          createPMSContact(params);
        }
      }
    });
  };

  return (
    <Modal
      visible={visible}
      onCancel={() => {
        resetFields();
        onClose();
      }}
      width={414}
      wrapClassName="pms-contact-modal"
      footer={null}
      confirmLoading={createLoading || updateLoading}
    >
      <div className="pms-contact-modal__content">
        <h3 className="pms-contact-modal__title">
          {type === 'create'
            ? t('cms:cms.pms_integrations.detail.contact_modal.create_title')
            : t('cms:cms.pms_integrations.detail.contact_modal.edit_title')}
        </h3>
        <Form>
          <Row>
            <Col>
              <Form.Item
                label={t('cms:cms.pms_integrations.detail.contact_modal.contact_name.label')}
              >
                {getFieldDecorator('contactName', {
                  initialValue: contactInfo?.contactName,
                  rules: [
                    {
                      required: true,
                      message: t(
                        'cms:cms.pms_integrations.detail.contact_modal.contact_name.required',
                      ),
                    },
                  ],
                })(
                  <Input
                    placeholder={t(
                      'cms:cms.pms_integrations.detail.contact_modal.contact_name.placeholder',
                    )}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item
                label={t('cms:cms.pms_integrations.detail.contact_modal.contact_email.label')}
              >
                {getFieldDecorator('contactEmail', {
                  initialValue: contactInfo?.contactEmail,
                  rules: [
                    {
                      validator: (_, value, callback) => {
                        const validateResult = validateEmail(value) && validateNotEmpty(value);

                        if (!validateResult && value) {
                          return callback(t('cms:cms.properties.edit.error.email'));
                        }
                        return callback();
                      },
                    },
                  ],
                })(
                  <Input
                    placeholder={t(
                      'cms:cms.pms_integrations.detail.contact_modal.contact_email.placeholder',
                    )}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item
                label={t('cms:cms.pms_integrations.detail.contact_modal.phone_number.label')}
              >
                {getFieldDecorator('contactPhone', {
                  initialValue: contactInfo?.contactPhone,
                  rules: [
                    {
                      validator: (_, value, callback) => {
                        const validateResult = validateInternationalTelephone(value);
                        if (!validateResult && value) {
                          return callback(t('cms:cms.properties.edit.error.phone'));
                        }
                        return callback();
                      },
                    },
                  ],
                })(
                  <PhoneNumber
                    placeholder={t(
                      'cms:cms.pms_integrations.detail.contact_modal.phone_number.placeholder',
                    )}
                    value={contactInfo?.contactPhone}
                    t={t}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item
                label={t('cms:cms.pms_integrations.detail.contact_modal.occupation.label')}
                style={{ paddingBottom: 8 }}
              >
                {getFieldDecorator('occupation', {
                  initialValue: contactInfo?.occupation,
                })(
                  <Input
                    placeholder={t(
                      'cms:cms.pms_integrations.detail.contact_modal.occupation.placeholder',
                    )}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Form.Item className="footer-bottom">
            <Button onClick={onClose} className="cancel-btn">
              {t('cms:cms.tag_modal.header.btn.cancel')}
            </Button>
            <Button type="primary" onClick={handleConfirmClick} className="confirm-btn">
              {t('cms:cms.tag_modal.header.btn.confirm')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}

ContactModal.propTypes = {
  t: PropTypes.func,
  onClose: PropTypes.func,
  visible: PropTypes.bool,
  form: PropTypes.object,
  type: PropTypes.string,
  refetch: PropTypes.func,
  contactInfo: PropTypes.object,
};

ContactModal.defaultProps = {
  t: () => {},
  onClose: () => {},
  visible: false,
  form: {},
  type: 'create',
  refetch: () => {},
  contactInfo: {},
};

export default Form.create({
  name: 'ContactModal',
})(ContactModal);
